import React, { useState, useRef, useEffect } from "react";
import {
  View,
  TextInput,
  Pressable,
  StyleSheet,
  ScrollView,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";
import { useTheme } from "@/hooks/useTheme";
import useContextMenu from "@/hooks/useContextMenu";
import Avatar from "@/components/Avatar";

// ── Types ────────────────────────────────────────────────────

type IconName = keyof typeof MaterialCommunityIcons.glyphMap;

export type ChipItem = {
  id: string;
  name: string;
  subtitle?: string;
  sourceLabel?: string;
  readonly: boolean;
  /** undefined = no expiry support; null = supports expiry but none set; string = ISO date */
  expiry?: string | null;
  /** undefined = no rate support; null = supports rate but none set; number = hourly rate */
  rate?: number | null;
  /** Avatar support (optional - if present, renders avatar instead of icon) */
  avatar?: {
    userId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
};

export type ChipOption = {
  id: string;
  name: string;
  description?: string;
  /** Avatar support for dropdown options */
  avatar?: {
    userId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
};

type AccentTone = "emerald" | "red" | "sky" | "amber" | "violet" | "slate";

export type FieldChipsState = "loading" | "idle" | "editing" | "saving";

type FieldChipsProps = {
  label: string;
  icon: IconName;
  accentTone: AccentTone;
  items: ChipItem[];
  options: ChipOption[];
  state: FieldChipsState;
  placeholder?: string;
  error?: string;
  onAdd: (optionId: string) => void;
  onRemove: (itemId: string) => void;
  onExpiryChange?: (itemId: string, date: string | null) => void;
  onRateChange?: (itemId: string, rate: number | null) => void;
  /** Called when a chip is pressed in idle state (for navigation) */
  onChipPress?: (itemId: string) => void;
};

// ── Helpers ──────────────────────────────────────────────────

function useAccentColors(tone: AccentTone) {
  const { colors } = useTheme();
  const map: Record<AccentTone, { bg: string; text: string }> = {
    emerald: { bg: colors.emerald.background, text: colors.emerald.text },
    red: { bg: colors.red.background, text: colors.red.text },
    sky: { bg: colors.sky.background, text: colors.sky.text },
    amber: { bg: colors.amber.background, text: colors.amber.text },
    violet: { bg: colors.violet.background, text: colors.violet.text },
    slate: { bg: colors.slate.background, text: colors.slate.text },
  };
  return map[tone];
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function isExpired(iso: string): boolean {
  return new Date(iso) < new Date();
}

const EXPIRY_PRESETS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "6 months", days: 182 },
  { label: "1 year", days: 365 },
] as const;

function formatRate(rate: number): string {
  return `$${rate.toFixed(2)}/hr`;
}

// ── Component ────────────────────────────────────────────────

export function FieldChips({
  label,
  icon,
  accentTone,
  items,
  options,
  state,
  placeholder = "Search\u2026",
  error,
  onAdd,
  onRemove,
  onExpiryChange,
  onRateChange,
  onChipPress,
}: FieldChipsProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const accent = useAccentColors(accentTone);
  const selectorRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const rateInputRef = useRef<TextInput>(null);
  const expiryRefs = useRef<Map<string, View>>(new Map());
  const rateRefs = useRef<Map<string, View>>(new Map());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rateInput, setRateInput] = useState("");
  const [rateEditingItem, setRateEditingItem] = useState<ChipItem | null>(null);

  const isLoading = state === "loading";
  const isEditing = state === "editing" || state === "saving";
  const isSaving = state === "saving";

  const handleOpen = () => {
    if (!isEditing || isSaving) return;

    contextMenu.setIgnoreRef(selectorRef);
    selectorRef.current?.measureInWindow((x, y, w, h) => {
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 2 });
      contextMenu.setStyle({
        backgroundColor: colors.card,
        overflow: "hidden",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
      });
      contextMenu.setMenuContent(
        <View style={{ minWidth: w }}>
          {(() => {
            const lower = query.toLowerCase();
            const filtered = query
              ? options.filter(
                  (o) => o.name.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower),
                )
              : options;
            return filtered.length === 0 ? (
              <View style={styles.emptyRow}>
                <MaterialCommunityIcons name="magnify-close" size={16} color={colors.textSecondary} />
                <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                  No results for &ldquo;{query}&rdquo;
                </ThemedText>
              </View>
            ) : (
              <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled">
                {filtered.map((opt) => {
                  const matchedItem = items.find((i) => i.name === opt.name);
                  const isAssignedDirect = matchedItem && !matchedItem.readonly;
                  const isInherited = matchedItem?.readonly;
                  const isDisabled = Boolean(matchedItem);

                  return (
                    <Pressable
                      key={opt.id}
                      style={({ hovered }) => [
                        styles.optionRow,
                        {
                          backgroundColor: isAssignedDirect
                            ? colors.primary + "15"
                            : !isDisabled && hovered
                              ? colors.backgroundSecondary
                              : "transparent",
                          opacity: isDisabled ? 0.5 : 1,
                        },
                      ]}
                      onPress={() => {
                        if (isDisabled) return;
                        console.log("[FieldChips] onAdd", opt.id, opt.name);
                        onAdd(opt.id);
                        // Stay open for multi-select — don't close
                      }}
                      disabled={isDisabled || isSaving}
                    >
                      {opt.avatar ? (
                        <Avatar
                          userId={opt.avatar.userId}
                          firstName={opt.avatar.firstName}
                          lastName={opt.avatar.lastName}
                          email={opt.avatar.email}
                          size={20}
                        />
                      ) : (
                        <MaterialCommunityIcons
                          name={icon}
                          size={16}
                          color={isAssignedDirect ? colors.primary : colors.textSecondary}
                        />
                      )}
                      <View style={styles.optionContent}>
                        <ThemedText
                          style={[
                            styles.optionName,
                            { color: isAssignedDirect ? colors.primary : colors.text },
                            isAssignedDirect && { fontWeight: "600" },
                          ]}
                        >
                          {opt.name}
                        </ThemedText>
                        {opt.description && (
                          <ThemedText style={[styles.optionDesc, { color: colors.textSecondary }]}>
                            {opt.description}
                          </ThemedText>
                        )}
                      </View>
                      {isInherited && (
                        <View style={[styles.inheritedBadge, { backgroundColor: colors.backgroundSecondary }]}>
                          <ThemedText style={[styles.inheritedText, { color: colors.textSecondary }]}>
                            Inherited
                          </ThemedText>
                        </View>
                      )}
                      {isAssignedDirect && <MaterialCommunityIcons name="check" size={16} color={colors.primary} />}
                    </Pressable>
                  );
                })}
              </ScrollView>
            );
          })()}
        </View>,
      );
      contextMenu.setVisible(true);
    });
  };

  // Re-render menu content when query or items change
  useEffect(() => {
    if (open) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, items]);

  const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const key = e.nativeEvent.key;
    const nativeEvent = e.nativeEvent as unknown as KeyboardEvent;
    if (key === "Enter" && !nativeEvent.shiftKey) {
      e.preventDefault();
      const lower = query.toLowerCase();
      const filtered = query
        ? options.filter((o) => o.name.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower))
        : options;
      const available = filtered.find((opt) => !items.some((i) => i.name === opt.name));
      if (available) {
        onAdd(available.id);
        setQuery("");
      }
    }
  };

  const openExpiryMenu = (item: ChipItem) => {
    if (!isEditing || isSaving || !onExpiryChange) return;

    // Close the search dropdown
    setOpen(false);
    setQuery("");

    const el = expiryRefs.current.get(item.id);
    if (!el) return;

    el.measureInWindow((x, y, w, h) => {
      contextMenu.setIgnoreRef({ current: el } as React.RefObject<View>);
      contextMenu.setLocation({ x: x - 120, y: y + (h ?? 0) + 4 });
      contextMenu.setStyle({
        backgroundColor: colors.card,
        overflow: "hidden",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
      });
      contextMenu.setMenuContent(
        <View style={{ minWidth: 180, paddingVertical: 4 }}>
          <View style={{ paddingHorizontal: 10, paddingVertical: 6 }}>
            <ThemedText style={[styles.label, { color: colors.textSecondary }]}>SET EXPIRY — {item.name}</ThemedText>
          </View>
          {item.expiry && (
            <View style={{ paddingHorizontal: 10, paddingBottom: 4 }}>
              <ThemedText style={{ fontSize: 12, color: isExpired(item.expiry) ? colors.red.text : colors.amber.text }}>
                Current: {formatExpiry(item.expiry)}
                {isExpired(item.expiry) ? " (expired)" : ""}
              </ThemedText>
            </View>
          )}
          {EXPIRY_PRESETS.map((preset) => (
            <Pressable
              key={preset.label}
              style={({ hovered }) => [
                styles.optionRow,
                { backgroundColor: hovered ? colors.backgroundSecondary : "transparent" },
              ]}
              onPress={() => {
                const date = new Date();
                date.setDate(date.getDate() + preset.days);
                onExpiryChange(item.id, date.toISOString());
                contextMenu.setVisible(false);
              }}
            >
              <MaterialCommunityIcons name="calendar-plus" size={14} color={colors.textSecondary} />
              <ThemedText style={[styles.optionName, { color: colors.text }]}>{preset.label}</ThemedText>
            </Pressable>
          ))}
          {item.expiry && (
            <Pressable
              style={({ hovered }) => [
                styles.optionRow,
                { backgroundColor: hovered ? colors.backgroundSecondary : "transparent" },
              ]}
              onPress={() => {
                onExpiryChange(item.id, null);
                contextMenu.setVisible(false);
              }}
            >
              <MaterialCommunityIcons name="calendar-remove" size={14} color={colors.red.text} />
              <ThemedText style={[styles.optionName, { color: colors.red.text }]}>Clear expiry</ThemedText>
            </Pressable>
          )}
        </View>,
      );
      contextMenu.setVisible(true);
    });
  };

  const renderRateMenu = (item: ChipItem, inputVal: string, rect: { x: number; y: number; h: number }) => {
    if (!onRateChange) return;
    contextMenu.setMenuContent(
      <View style={{ minWidth: 200, paddingVertical: 4 }}>
        <View style={{ paddingHorizontal: 10, paddingVertical: 6 }}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>HOURLY RATE — {item.name}</ThemedText>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 10, paddingVertical: 6 }}>
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              backgroundColor: colors.backgroundSecondary,
              borderRadius: 6,
              borderWidth: StyleSheet.hairlineWidth,
              borderColor: colors.border,
              paddingHorizontal: 8,
              paddingVertical: 6,
            }}
          >
            <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>$</ThemedText>
            <TextInput
              ref={rateInputRef}
              value={inputVal}
              onChangeText={(text) => {
                // Allow digits and at most one decimal point
                const cleaned = text.replace(/[^0-9.]/g, "").replace(/(\..*?)\./, "$1");
                setRateInput(cleaned);
              }}
              placeholder="0.00"
              placeholderTextColor={colors.placeholder}
              style={[styles.rateInput, { color: colors.text }]}
              keyboardType="decimal-pad"
              autoCapitalize="none"
              autoCorrect={false}
            />
            <ThemedText style={{ fontSize: 11, color: colors.textSecondary }}>/hr</ThemedText>
          </View>
          <Pressable
            style={({ hovered }) => [
              styles.rateSetButton,
              {
                backgroundColor: hovered ? colors.primary + "DD" : colors.primary,
              },
            ]}
            onPress={() => {
              const parsed = parseFloat(inputVal);
              if (!isNaN(parsed) && parsed > 0) {
                onRateChange(item.id, parsed);
              }
              setRateEditingItem(null);
              contextMenu.setVisible(false);
            }}
          >
            <ThemedText style={{ fontSize: 12, fontWeight: "600", color: "#fff" }}>Set</ThemedText>
          </Pressable>
        </View>
        {item.rate != null && (
          <Pressable
            style={({ hovered }) => [
              styles.optionRow,
              { backgroundColor: hovered ? colors.backgroundSecondary : "transparent" },
            ]}
            onPress={() => {
              onRateChange(item.id, null);
              setRateEditingItem(null);
              contextMenu.setVisible(false);
            }}
          >
            <MaterialCommunityIcons name="currency-usd-off" size={14} color={colors.red.text} />
            <ThemedText style={[styles.optionName, { color: colors.red.text }]}>Clear rate</ThemedText>
          </Pressable>
        )}
      </View>,
    );
  };

  const openRateMenu = (item: ChipItem) => {
    if (!isEditing || isSaving || !onRateChange) return;

    setOpen(false);
    setQuery("");

    const initialVal = item.rate != null ? item.rate.toFixed(2) : "";
    setRateInput(initialVal);
    setRateEditingItem(item);

    const el = rateRefs.current.get(item.id);
    if (!el) return;

    el.measureInWindow((x, y, _w, h) => {
      contextMenu.setIgnoreRef({ current: el } as React.RefObject<View>);
      contextMenu.setLocation({ x: x - 140, y: y + (h ?? 0) + 4 });
      contextMenu.setStyle({
        backgroundColor: colors.card,
        overflow: "hidden",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
      });
      renderRateMenu(item, initialVal, { x, y, h: h ?? 0 });
      contextMenu.setVisible(true);
      setTimeout(() => rateInputRef.current?.focus(), 100);
    });
  };

  // Re-render rate menu when input changes
  useEffect(() => {
    if (rateEditingItem) {
      renderRateMenu(rateEditingItem, rateInput, { x: 0, y: 0, h: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateInput]);

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <View style={styles.field}>
        <Skeleton width={70} height={10} />
        <View style={styles.chipRow}>
          <Skeleton width={90} height={24} radius={6} />
          <Skeleton width={110} height={24} radius={6} />
          <Skeleton width={80} height={24} radius={6} />
        </View>
      </View>
    );
  }

  const hasError = Boolean(error);

  return (
    <View style={[styles.field, { opacity: isSaving ? 0.5 : 1 }]}>
      {/* Label */}
      <ThemedText style={[styles.label, { color: colors.textSecondary }]}>
        {label} ({items.length})
      </ThemedText>

      {/* Chip area */}
      <View
        ref={selectorRef}
        style={[
          styles.chipArea,
          {
            backgroundColor: colors.backgroundSecondary,
            borderColor: open ? colors.primary : hasError ? colors.red.text : colors.border,
            borderWidth: open ? 1.5 : StyleSheet.hairlineWidth,
          },
        ]}
      >
        <View style={styles.chipFlow}>
          {items.map((item) => {
            const isClickable = !isEditing && onChipPress;
            const ChipWrapper = isClickable ? Pressable : View;
            const wrapperProps = isClickable
              ? {
                  onPress: () => onChipPress(item.id),
                  style: ({ hovered }: { hovered?: boolean }) => [
                    styles.chip,
                    {
                      backgroundColor: accent.bg + "60",
                      borderColor: hovered ? colors.primary : accent.bg,
                      borderWidth: hovered ? 1 : StyleSheet.hairlineWidth,
                      cursor: "pointer",
                    },
                  ],
                }
              : {
                  style: [
                    styles.chip,
                    {
                      backgroundColor: accent.bg + "60",
                      borderColor: accent.bg,
                      borderWidth: StyleSheet.hairlineWidth,
                    },
                  ],
                };
            return (
              <ChipWrapper key={item.id} {...(wrapperProps as any)}>
                {item.avatar ? (
                  <Avatar
                    userId={item.avatar.userId}
                    firstName={item.avatar.firstName}
                    lastName={item.avatar.lastName}
                    email={item.avatar.email}
                    size={18}
                  />
                ) : (
                  <MaterialCommunityIcons name={icon} size={13} color={accent.text} />
                )}
                <ThemedText style={[styles.chipText, { color: colors.text }]}>{item.name}</ThemedText>
                {item.sourceLabel && (
                  <View style={[styles.chipBadge, { backgroundColor: colors.sky.background }]}>
                    <ThemedText style={[styles.chipBadgeText, { color: colors.sky.text }]}>
                      {item.sourceLabel}
                    </ThemedText>
                  </View>
                )}
                {item.expiry != null && (
                  <View
                    style={[
                      styles.chipBadge,
                      {
                        backgroundColor: isExpired(item.expiry) ? colors.red.background : colors.amber.background,
                      },
                    ]}
                  >
                    <ThemedText
                      style={[
                        styles.chipBadgeText,
                        { color: isExpired(item.expiry) ? colors.red.text : colors.amber.text },
                      ]}
                    >
                      {formatExpiry(item.expiry)}
                    </ThemedText>
                  </View>
                )}
                {item.rate != null && (
                  <View style={[styles.chipBadge, { backgroundColor: colors.emerald.background }]}>
                    <ThemedText style={[styles.chipBadgeText, { color: colors.emerald.text }]}>
                      {formatRate(item.rate)}
                    </ThemedText>
                  </View>
                )}
                {isEditing && !item.readonly && onExpiryChange && item.expiry !== undefined && (
                  <Pressable
                    ref={(el) => {
                      if (el) expiryRefs.current.set(item.id, el);
                      else expiryRefs.current.delete(item.id);
                    }}
                    onPress={() => openExpiryMenu(item)}
                    hitSlop={6}
                    style={{ padding: 1 }}
                  >
                    <MaterialCommunityIcons
                      name="calendar-clock"
                      size={13}
                      color={item.expiry ? colors.amber.text : colors.textSecondary}
                      style={{ opacity: isSaving ? 0.3 : 0.6 }}
                    />
                  </Pressable>
                )}
                {isEditing && !item.readonly && onRateChange && item.rate !== undefined && (
                  <Pressable
                    ref={(el) => {
                      if (el) rateRefs.current.set(item.id, el);
                      else rateRefs.current.delete(item.id);
                    }}
                    onPress={() => openRateMenu(item)}
                    hitSlop={6}
                    style={{ padding: 1 }}
                  >
                    <MaterialCommunityIcons
                      name="currency-usd"
                      size={13}
                      color={item.rate != null ? colors.emerald.text : colors.textSecondary}
                      style={{ opacity: isSaving ? 0.3 : 0.6 }}
                    />
                  </Pressable>
                )}
                {isEditing && !item.readonly && (
                  <Pressable onPress={() => onRemove(item.id)} hitSlop={6} style={{ padding: 1 }}>
                    <MaterialCommunityIcons
                      name="close-circle"
                      size={14}
                      color={colors.textSecondary}
                      style={{ opacity: isSaving ? 0.3 : 0.6 }}
                    />
                  </Pressable>
                )}
              </ChipWrapper>
            );
          })}

          {/* Inline search input when editing */}
          {isEditing && !isSaving && (
            <TextInput
              ref={inputRef}
              value={query}
              onChangeText={setQuery}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                setOpen(true);
                setTimeout(handleOpen, 50);
              }}
              onBlur={() => {
                setTimeout(() => {
                  setOpen(false);
                  setQuery("");
                  contextMenu.setVisible(false);
                }, 150);
              }}
              placeholder={items.length === 0 ? placeholder : ""}
              placeholderTextColor={colors.placeholder}
              style={[styles.inlineInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isSaving}
            />
          )}
        </View>

        {/* Chevron affordance */}
        {isEditing && !isSaving && (
          <Pressable
            onPress={() => {
              if (open) {
                setOpen(false);
                setQuery("");
                contextMenu.setVisible(false);
              } else {
                setOpen(true);
                setTimeout(() => inputRef.current?.focus(), 50);
              }
            }}
            hitSlop={8}
            style={{ padding: 2, alignSelf: "center" }}
          >
            <MaterialCommunityIcons
              name={open ? "chevron-up" : "chevron-down"}
              size={16}
              color={colors.textSecondary}
              style={{ opacity: 0.5 }}
            />
          </Pressable>
        )}
      </View>

      {/* Error text */}
      {hasError && (
        <View style={styles.errorRow}>
          <MaterialCommunityIcons name="alert-circle-outline" size={12} color={colors.red.text} />
          <ThemedText style={[styles.errorText, { color: colors.red.text }]}>{error}</ThemedText>
        </View>
      )}
    </View>
  );
}

// ── Styles ───────────────────────────────────────────────────

const styles = StyleSheet.create({
  field: {
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  chipArea: {
    flexDirection: "row",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    minHeight: 40,
    gap: 6,
  },
  chipFlow: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
  },
  chipText: {
    fontSize: 12,
  },
  chipBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  chipBadgeText: {
    fontSize: 9,
    fontWeight: "600",
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  inlineInput: {
    fontSize: 13,
    minWidth: 80,
    flex: 1,
    // @ts-ignore web-only
    outlineStyle: "none",
    padding: 0,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  optionContent: {
    flex: 1,
    gap: 1,
  },
  optionName: {
    fontSize: 13,
  },
  optionDesc: {
    fontSize: 11,
  },
  inheritedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  inheritedText: {
    fontSize: 9,
    fontWeight: "600",
  },
  emptyRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  emptyText: {
    fontSize: 12,
  },
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 2,
  },
  errorText: {
    fontSize: 11,
    fontWeight: "500",
  },
  rateInput: {
    flex: 1,
    fontSize: 13,
    padding: 0,
    // @ts-ignore web-only
    outlineStyle: "none",
  },
  rateSetButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
});
